import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  IconButton,
  Divider,
  useToast,
  Flex,
  Heading,
  Badge,
  useColorModeValue,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Radio,
  RadioGroup,
  Stack,
  Icon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FiTrash2, FiArrowLeft, FiLock, FiCreditCard, FiTruck, FiShield, FiCheck, FiUser, FiMapPin, FiPhone } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import type { Product } from "../types";
import jazzcashCard from "../images/paymentlogo/jazzcash-card.png";
import jazzcashMobile from "../images/paymentlogo/jazzcash-mobile.png";
import easypaisa from "../images/paymentlogo/easypaisa.png";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Checkout: React.FC = () => {
  const { items: cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const selectedBorderColor = "purple.500";
  const selectedBgColor = "purple.50";

  const [paymentMethod, setPaymentMethod] = useState<"jazzcash-card" | "jazzcash-mobile" | "easypaisa">("jazzcash-card");
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    cardNumber: "",
    cardName: "",
    expiryDate: "",
    cvv: "",
    country: "pakistan",
  });

  const total = cart.reduce<number>((sum, item) => {
    return sum + item.price * item.quantity;
  }, 0);

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      toast({
        title: "Item removed",
        description: "The item has been removed from your cart",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: number) => {
    removeFromCart(productId);
    toast({
      title: "Item removed",
      description: "The item has been removed from your cart",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      clearCart();
      navigate("/order-success");
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Please try again or choose a different payment method.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <Container maxW="container.md" py={20}>
        <VStack spacing={8} textAlign="center">
          <Heading size="2xl" color="purple.700">
            Your Cart is Empty
          </Heading>
          <Text fontSize="xl" color="gray.600">
            Please add items to your cart before proceeding to checkout
          </Text>
          <Button
            colorScheme="purple"
            size="lg"
            onClick={() => navigate("/categories")}
            leftIcon={<FiArrowLeft />}
          >
            Continue Shopping
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Box py={12}>
      <Container maxW="1400px">
        <VStack spacing={8} align="stretch">
          {/* Checkout Header */}
          <Box textAlign="center" mb={8}>
            <Heading size="2xl" color="purple.700" mb={4}>
              Checkout
            </Heading>
            <Text fontSize="xl" color="purple.500">
              Complete your purchase securely
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
            {/* Order Summary */}
            <Box
              gridColumn={{ base: "1", lg: "span 1" }}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              shadow="md"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Heading size="lg" color="purple.700" mb={6}>
                Order Summary
              </Heading>
              <VStack spacing={4} align="stretch">
                {cart.map((item) => (
                  <MotionBox
                    key={item.id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <Box
                      bg={bgColor}
                      borderRadius="lg"
                      p={4}
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack spacing={4} align="center">
                        <Box
                          position="relative"
                          width="80px"
                          height="80px"
                          flexShrink={0}
                        >
                          <Image
                            src={item.image}
                            alt={item.name}
                            borderRadius="md"
                            objectFit="cover"
                            width="100%"
                            height="100%"
                          />
                          <Badge
                            position="absolute"
                            top={2}
                            right={2}
                            colorScheme="purple"
                            borderRadius="full"
                            px={2}
                            py={1}
                          >
                            {item.quantity}
                          </Badge>
                        </Box>
                        <VStack flex={1} align="start" spacing={1}>
                          <Text fontWeight="bold" color="purple.700">
                            {item.name}
                          </Text>
                          <Text color="gray.600" fontSize="sm">
                            {item.description}
                          </Text>
                          <HStack spacing={4}>
                            <Text color="purple.600" fontWeight="bold">
                              Rs. {item.price.toLocaleString()}
                            </Text>
                            {item.originalPrice && (
                              <Text
                                color="gray.400"
                                textDecoration="line-through"
                                fontSize="sm"
                              >
                                Rs. {item.originalPrice.toLocaleString()}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                        <VStack spacing={2} align="end">
                          <NumberInput
                            min={1}
                            max={10}
                            value={item.quantity}
                            onChange={(value: string) =>
                              handleQuantityChange(item.id, parseInt(value))
                            }
                            size="sm"
                            width="100px"
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <IconButton
                            aria-label="Remove item"
                            icon={<FiTrash2 />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleRemoveItem(item.id)}
                            size="sm"
                          />
                        </VStack>
                      </HStack>
                    </Box>
                  </MotionBox>
                ))}
              </VStack>
            </Box>

            {/* Checkout Form */}
            <Box
              gridColumn={{ base: "1", lg: "span 2" }}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              shadow="md"
              borderWidth="1px"
              borderColor={borderColor}
              height="fit-content"
            >
              <Heading size="lg" color="purple.700" mb={6}>
                Shipping Details
              </Heading>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>First Name</FormLabel>
                      <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Last Name</FormLabel>
                      <Input
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+92 300 1234567"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Address</FormLabel>
                    <Textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Your complete address"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Country</FormLabel>
                    <Select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="Select country"
                    >
                      <option value="pakistan">Pakistan</option>
                      <option value="us">United States</option>
                      <option value="australia">Australia</option>
                      <option value="uk">United Kingdom</option>
                      <option value="canada">Canada</option>
                    </Select>
                  </FormControl>

                  <SimpleGrid columns={3} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>City</FormLabel>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>State</FormLabel>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>ZIP Code</FormLabel>
                      <Input
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="ZIP"
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Divider my={4} />

                  <Heading size="md" color="purple.700" mb={4}>
                    Payment Method
                  </Heading>
                  <VStack spacing={4} align="stretch">
                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={
                        paymentMethod === "jazzcash-card" ? selectedBorderColor : borderColor
                      }
                      cursor="pointer"
                      _hover={{ borderColor: selectedBorderColor }}
                      onClick={() => setPaymentMethod("jazzcash-card")}
                      bg={
                        paymentMethod === "jazzcash-card" ? selectedBgColor : "transparent"
                      }
                    >
                      <Radio
                        value="jazzcash-card"
                        isChecked={paymentMethod === "jazzcash-card"}
                      >
                        <HStack spacing={3}>
                          <Image
                            src={jazzcashCard}
                            alt="JazzCash Debit/Credit Card"
                            height="24px"
                            objectFit="contain"
                          />
                          <Text fontWeight="bold">JazzCash Debit/Credit Card</Text>
                        </HStack>
                      </Radio>
                    </Box>

                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={
                        paymentMethod === "jazzcash-mobile" ? selectedBorderColor : borderColor
                      }
                      cursor="pointer"
                      _hover={{ borderColor: selectedBorderColor }}
                      onClick={() => setPaymentMethod("jazzcash-mobile")}
                      bg={
                        paymentMethod === "jazzcash-mobile"
                          ? selectedBgColor
                          : "transparent"
                      }
                    >
                      <Radio
                        value="jazzcash-mobile"
                        isChecked={paymentMethod === "jazzcash-mobile"}
                      >
                        <HStack spacing={3}>
                          <Image
                            src={jazzcashMobile}
                            alt="JazzCash Mobile"
                            height="24px"
                            objectFit="contain"
                          />
                          <Text fontWeight="bold">JazzCash Mobile</Text>
                        </HStack>
                      </Radio>
                    </Box>

                    <Box
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderColor={
                        paymentMethod === "easypaisa" ? selectedBorderColor : borderColor
                      }
                      cursor="pointer"
                      _hover={{ borderColor: selectedBorderColor }}
                      onClick={() => setPaymentMethod("easypaisa")}
                      bg={paymentMethod === "easypaisa" ? selectedBgColor : "transparent"}
                    >
                      <Radio
                        value="easypaisa"
                        isChecked={paymentMethod === "easypaisa"}
                      >
                        <HStack spacing={3}>
                          <Image
                            src={easypaisa}
                            alt="Easypaisa"
                            height="24px"
                            objectFit="contain"
                          />
                          <Text fontWeight="bold">Easypaisa</Text>
                        </HStack>
                      </Radio>
                    </Box>
                  </VStack>

                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      To change or edit your order, go back. No changes will be
                      allowed once you click "PLACE ORDER".
                    </Text>
                  </Alert>

                  <Divider my={4} />

                  <VStack spacing={4} align="stretch">
                    <Flex justify="space-between">
                      <Text color="gray.600">Subtotal</Text>
                      <Text color="purple.600" fontWeight="bold">
                        Rs. {total.toLocaleString()}
                      </Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text color="gray.600">Shipping</Text>
                      <Text color="green.500" fontWeight="bold">
                        Free
                      </Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text fontSize="lg" fontWeight="bold" color="purple.700">
                        Total
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="purple.700">
                        Rs. {total.toLocaleString()}
                      </Text>
                    </Flex>

                    <Button
                      type="submit"
                      colorScheme="purple"
                      size="lg"
                      leftIcon={<FiLock />}
                      mt={4}
                      isLoading={isProcessing}
                      loadingText="Processing Payment"
                    >
                      Place Order
                    </Button>
                  </VStack>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Checkout;
